<!-- warehouse_scan/public/js/components/ScanArea.vue -->
<template>
  <div class="scan-area">
    <!-- 模式切换 -->
    <div class="mode-switch">
      <span class="mode-label">
        <span class="mode-icon">📷</span>
        扫描模式
      </span>
      <div class="mode-buttons">
        <button 
          class="mode-btn" 
          :class="{ active: currentMode === 'scanner' }"
          @click="switchMode('scanner')"
        >
          🔘 扫描头
        </button>
        <button 
          class="mode-btn" 
          :class="{ active: currentMode === 'camera' }"
          @click="switchMode('camera')"
        >
          📱 相机
        </button>
      </div>
    </div>

    <!-- 扫描头模式 -->
    <div v-if="currentMode === 'scanner'" class="scanner-mode">
      <div class="input-wrapper">
        <input 
          ref="scanInputRef"
          type="text" 
          v-model="inputValue"
          :placeholder="placeholder"
          :disabled="disabled"
          class="scan-input"
          @keyup.enter="handleScan"
          @focus="onFocus"
          @blur="onBlur"
        >
        <span class="scan-icon">📷</span>
      </div>
      <div class="scanner-hint">
        <span class="hint-icon">ℹ️</span>
        {{ hintText }}
      </div>
    </div>

    <!-- 相机模式 -->
    <div v-else class="camera-mode">
      <div class="camera-container">
        <video 
          ref="videoElement"
          class="camera-video"
          playsinline
          autoplay
        ></video>
        <div class="scan-overlay"></div>
        <div v-if="!isCameraActive" class="camera-placeholder">
          <button class="start-camera-btn" @click="startCamera">
            <span class="btn-icon">📷</span>
            开启相机扫码
          </button>
          <p class="camera-tip">请允许相机权限后使用</p>
        </div>
      </div>
      <div v-if="isCameraActive" class="camera-controls">
        <button class="camera-btn" @click="stopCamera">
          <span>⏹️</span> 停止
        </button>
        <button class="camera-btn" @click="switchCamera">
          <span>🔄</span> {{ facingMode === 'environment' ? '前置' : '后置' }}
        </button>
      </div>
      <div class="camera-hint">
        将二维码放入扫描框内自动识别
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';

const props = defineProps({
  mode: {
    type: String,
    default: 'scanner'
  },
  disabled: {
    type: Boolean,
    default: false
  },
  placeholder: {
    type: String,
    default: '请扫描二维码...'
  }
});

const emit = defineEmits(['scan', 'mode-change']);

const currentMode = ref(props.mode);
const inputValue = ref('');
const scanInputRef = ref(null);
const videoElement = ref(null);
const isCameraActive = ref(false);
const facingMode = ref('environment');

let stream = null;
let codeReader = null;
let scanBuffer = '';
let scanTimeout = null;
let lastKeyTime = 0;
let isScanGunDetected = false;

const SCAN_KEY_INTERVAL = 50;
const SCAN_MIN_LENGTH = 3;
const SCAN_END_TIMEOUT = 120;

const hintText = computed(() => {
  if (props.disabled) return '请先选择出入库类型';
  return '扫描枪无需聚焦，直接扫描即可；也可手动输入后按回车';
});

// 切换模式
const switchMode = (mode) => {
  currentMode.value = mode;
  emit('mode-change', mode);
  
  if (mode === 'scanner') {
    stopCamera();
    nextTick(() => {
      if (scanInputRef.value) {
        scanInputRef.value.focus();
      }
    });
  } else {
    startCamera();
  }
};

// 处理扫描
const handleScan = () => {
  if (!inputValue.value || props.disabled) return;
  emit('scan', inputValue.value);
  inputValue.value = '';
};

// 设置扫描枪监听（全局键盘事件）
const setupScannerListener = () => {
  document.addEventListener('keydown', (e) => {
    if (currentMode.value !== 'scanner') return;
    if (props.disabled) return;
    if (e.ctrlKey || e.altKey || e.metaKey) return;

    // 如果焦点在输入框（如 QtyDialog 的数量输入），不拦截，让用户正常手动输入
    const activeEl = document.activeElement;
    if (activeEl && (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA' || activeEl.tagName === 'SELECT')) {
      return;
    }

    const now = Date.now();

    if (e.key === 'Enter') {
      if (isScanGunDetected && scanBuffer.length >= SCAN_MIN_LENGTH) {
        e.preventDefault();
        e.stopPropagation();
        emit('scan', scanBuffer);
        inputValue.value = '';
        scanBuffer = '';
        isScanGunDetected = false;
        lastKeyTime = 0;
      }
      return;
    }

    if (e.key.length !== 1) return;

    if (scanBuffer.length > 0 && (now - lastKeyTime) < SCAN_KEY_INTERVAL) {
      isScanGunDetected = true;
    } else if (scanBuffer.length > 0 && (now - lastKeyTime) > SCAN_KEY_INTERVAL * 3) {
      scanBuffer = '';
      isScanGunDetected = false;
    }

    lastKeyTime = now;
    scanBuffer += e.key;

    if (isScanGunDetected) {
      e.preventDefault();
      e.stopPropagation();
    }

    clearTimeout(scanTimeout);
    scanTimeout = setTimeout(() => {
      if (isScanGunDetected && scanBuffer.length >= SCAN_MIN_LENGTH) {
        emit('scan', scanBuffer);
        inputValue.value = '';
      }
      scanBuffer = '';
      isScanGunDetected = false;
      lastKeyTime = 0;
    }, SCAN_END_TIMEOUT);
  }, true);
};

// 启动相机
const startCamera = async () => {
  try {
    const constraints = {
      video: {
        facingMode: facingMode.value,
        width: { ideal: 1280 },
        height: { ideal: 720 }
      }
    };
    
    stream = await navigator.mediaDevices.getUserMedia(constraints);
    if (videoElement.value) {
      videoElement.value.srcObject = stream;
      await videoElement.value.play();
      isCameraActive.value = true;
      initCodeReader();
    }
  } catch (err) {
    console.error('相机启动失败:', err);
    if (facingMode.value === 'environment') {
      try {
        const defaultConstraints = { video: true };
        stream = await navigator.mediaDevices.getUserMedia(defaultConstraints);
        if (videoElement.value) {
          videoElement.value.srcObject = stream;
          await videoElement.value.play();
          isCameraActive.value = true;
          initCodeReader();
        }
      } catch (e) {
        frappe.show_alert({
          message: '无法启动相机，请检查权限设置',
          indicator: 'red'
        });
      }
    } else {
      facingMode.value = 'environment';
      frappe.show_alert({
        message: '前置摄像头不可用，已切回后置',
        indicator: 'orange'
      });
    }
  }
};

// 停止相机
const stopCamera = () => {
  if (stream) {
    stream.getTracks().forEach(track => track.stop());
    stream = null;
  }
  if (codeReader) {
    try {
      codeReader.stop();
      codeReader = null;
    } catch (e) {}
  }
  isCameraActive.value = false;
};

// 切换相机
const switchCamera = () => {
  facingMode.value = facingMode.value === 'environment' ? 'user' : 'environment';
  stopCamera();
  setTimeout(() => startCamera(), 100);
};

// 初始化条码识别器
const initCodeReader = () => {
  if (!window.ZXing) {
    // 动态加载ZXing库
    const script = document.createElement('script');
    script.src = '/assets/warehouse_scan/js/lib/ZXing.min.js';
    script.onload = () => {
      setTimeout(initCodeReader, 100);
    };
    document.head.appendChild(script);
    return;
  }

  try {
    codeReader = new ZXing.BrowserMultiFormatReader();
    codeReader.decodeFromVideoDevice(null, 'video', (result) => {
      if (result && !props.disabled) {
        emit('scan', result.text);
        
        // 震动反馈
        if (navigator.vibrate) {
          navigator.vibrate(100);
        }
        
        // 暂停1.5秒避免重复扫描
        codeReader.stop();
        setTimeout(() => {
          if (currentMode.value === 'camera' && isCameraActive.value) {
            initCodeReader();
          }
        }, 1500);
      }
    });
  } catch (err) {
    console.error('初始化扫码器失败:', err);
  }
};

// 聚焦事件
const onFocus = () => {
  if (navigator.vibrate) navigator.vibrate(20);
};

const onBlur = () => {
  // 延迟重新聚焦（扫描枪模式下）
  // 但如果焦点转移到了其他输入框（如 QtyDialog），不要抢回焦点
  setTimeout(() => {
    const activeEl = document.activeElement;
    if (activeEl && (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA' || activeEl.tagName === 'SELECT')) {
      return; // 焦点在其他输入框上，不抢回
    }
    if (currentMode.value === 'scanner' && scanInputRef.value && !props.disabled) {
      scanInputRef.value.focus();
    }
  }, 100);
};

// 聚焦输入框（供父组件调用）
const focus = () => {
  if (currentMode.value === 'scanner' && scanInputRef.value) {
    scanInputRef.value.focus();
  }
};

// 监听模式变化
watch(() => props.mode, (newMode) => {
  currentMode.value = newMode;
  if (newMode === 'scanner') {
    stopCamera();
    nextTick(() => focus());
  }
});

// 监听禁用状态
watch(() => props.disabled, (newVal) => {
  if (newVal) {
    if (scanInputRef.value) {
      scanInputRef.value.blur();
    }
  } else {
    focus();
  }
});

// 暴露方法给父组件
defineExpose({
  focus
});

// 生命周期
onMounted(() => {
  setupScannerListener();
  if (currentMode.value === 'scanner') {
    nextTick(() => focus());
  }
});

onUnmounted(() => {
  stopCamera();
  clearTimeout(scanTimeout);
  scanBuffer = '';
  isScanGunDetected = false;
  lastKeyTime = 0;
});
</script>

<style scoped>
.scan-area {
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 15px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.mode-switch {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 15px;
  padding-bottom: 15px;
  border-bottom: 1px solid #f1f3f5;
}

.mode-label {
  font-weight: 500;
  color: #2c3e50;
  display: flex;
  align-items: center;
  gap: 6px;
}

.mode-icon {
  font-size: 18px;
}

.mode-buttons {
  display: flex;
  gap: 5px;
  background: #f1f3f5;
  padding: 3px;
  border-radius: 30px;
}

.mode-btn {
  padding: 8px 20px;
  border: none;
  background: transparent;
  border-radius: 25px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  color: #6c757d;
}

.mode-btn.active {
  background: white;
  color: #007bff;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

/* 扫描头模式 */
.scanner-mode .input-wrapper {
  position: relative;
  margin-bottom: 10px;
}

.scan-input {
  width: 100%;
  padding: 15px 50px 15px 15px;
  font-size: 18px;
  border: 2px solid #dee2e6;
  border-radius: 12px;
  outline: none;
  transition: all 0.3s;
  background: #f8f9fa;
}

.scan-input:focus {
  border-color: #007bff;
  background: white;
  box-shadow: 0 0 0 3px rgba(0,123,255,0.1);
}

.scan-input:disabled {
  background: #e9ecef;
  cursor: not-allowed;
  opacity: 0.7;
}

.scan-icon {
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
  font-size: 20px;
}

.scanner-hint {
  text-align: center;
  color: #6c757d;
  font-size: 12px;
  padding: 8px;
  background: #f1f3f5;
  border-radius: 8px;
}

.hint-icon {
  margin-right: 5px;
  color: #007bff;
}

/* 相机模式 */
.camera-mode .camera-container {
  position: relative;
  width: 100%;
  height: 300px;
  background: #2c3e50;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 15px;
}

.camera-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.scan-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border: 2px solid #00ff00;
  box-shadow: 0 0 0 9999px rgba(0,0,0,0.3);
  pointer-events: none;
}

.camera-placeholder {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(0,0,0,0.6);
  color: white;
}

.start-camera-btn {
  padding: 12px 25px;
  background: #007bff;
  border: none;
  border-radius: 30px;
  color: white;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
}

.start-camera-btn .btn-icon {
  font-size: 20px;
}

.camera-tip {
  margin-top: 10px;
  font-size: 12px;
  opacity: 0.8;
}

.camera-controls {
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-bottom: 10px;
}

.camera-btn {
  padding: 10px 20px;
  border: none;
  border-radius: 30px;
  font-size: 14px;
  cursor: pointer;
  background: #f1f3f5;
  color: #495057;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 5px;
}

.camera-btn:hover {
  background: #e9ecef;
}

.camera-hint {
  text-align: center;
  color: #6c757d;
  font-size: 12px;
}
</style>